import { BaseType, pointer, Selection } from "d3";
import { produce } from "immer";
import React from "react";

import { roundToGrid } from "@examspark/common/dist/utils/number-line";
import { Cartesian } from "@examspark/types";

import { D3NumberLine, NumberLineCoordinate } from "../d3Utils";

import { createListenPointCoordinate } from "./drawShapes";

const useListenerPoints = ({
  mode,
  shapes,
  onChange,
}: {
  mode: string | undefined;
  shapes?: Cartesian.Shape[];
  onChange?: (shapes: Cartesian.Shape[]) => void;
}) => {
  const [listenPoints, setListenPoints] = React.useState<
    Cartesian.Coordinate[]
  >([]);

  React.useEffect(() => {
    let newShape: Cartesian.Shape | undefined;
    switch (mode) {
      case "point":
      case "ray-left":
      case "ray-left-open":
      case "ray-right":
      case "ray-right-open":
        if (listenPoints.length >= 1) {
          newShape = {
            type: mode,
            coord: {
              x: listenPoints[0].x,
              y: 0,
            },
          };
        }
        break;
      case "line-segment":
      case "line-segment-left-open":
      case "line-segment-right-open":
      case "line-segment-both-open":
        {
          // ensure the points are sorted so that the left/right-open makes sense.
          const sortedListenPoints = [...listenPoints].sort(
            (a, b) => a.x - b.x,
          );
          if (listenPoints.length >= 2) {
            newShape = {
              type: mode,
              coords: [
                {
                  x: sortedListenPoints[0].x,
                  y: 0,
                },
                {
                  x: sortedListenPoints[1].x,
                  y: 0,
                },
              ],
            };
          }
        }
        break;
    }

    if (newShape) {
      setListenPoints([]);
      onChange?.(
        produce(shapes ?? [], (draft) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          draft.push(newShape!);
          return draft;
        }),
      );
    }
  }, [listenPoints, mode, onChange, shapes]);

  return [listenPoints, setListenPoints] as const;
};

export const useListenPointsDecorator = <GType extends BaseType>({
  mode,
  axis,
  onChange,
  shapes,
  margin,
}: {
  axis: D3NumberLine;
  mode: string | undefined;
  shapes?: Cartesian.Shape[];
  margin: { top: number; right: number; bottom: number; left: number };
  onChange?: (shapes: Cartesian.Shape[]) => void;
}) => {
  const [listenPoints, setListenPoints] = useListenerPoints({
    mode,
    shapes,
    onChange,
  });

  return (svg: Selection<GType, unknown, any, any>) => {
    const should_render = !!mode && mode !== "erase";

    const listen_points = svg
      .selectAll("g.graph-listen-points")
      // passing an empty array here will remove the element
      .data([should_render].filter(Boolean))
      .join("g")
      .attr("class", "graph-listen-points");

    listen_points
      .selectAll("circle")
      .data<NumberLineCoordinate>(listenPoints)
      .join("circle")
      .call(createListenPointCoordinate(axis));

    const handleCreateShapeClick = (e: React.MouseEvent<SVGElement>) => {
      // get coordinates relative to the axis svg.
      const [pointerX] = pointer(e, svg.select(".axis").node());

      setListenPoints((listenPoints) =>
        listenPoints.concat({
          x: roundToGrid(axis.scale.invert(pointerX), axis),
          y: 0,
        }),
      );
    };

    svg.selectAll(".graph-interaction").remove();

    if (should_render) {
      svg
        .append("rect")
        .attr("class", "graph-interaction")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("transform", `translate(${-margin.left}, ${-margin.top})`)
        .attr("fill", "transparent")
        .on("click", handleCreateShapeClick);
    }
  };
};
